import { gpsDrivePost } from "@/lib/api/gpsdrive-client";
import type { AccountType } from "./gpsdrive-users.service";

// Resumo em lote da tela de Usuários (aba principal, ver routes/users.tsx
// #UserRow) — uma resposta só cobrindo os campos que antes cada linha da
// tabela buscava sozinha (5-7 requests próprios: avatar, dispositivos
// vinculados, identidade, perfil Cliente/Financeiro, faturas, e mais dois
// só de "aquecimento" de cache). Ver diagnóstico de lentidão 2026-09-11:
// a resolução de identidade por linha refazia a busca da lista INTEIRA de
// usuários do Traccar a cada linha — N usuários na tabela, N+1 chamadas
// ao Traccar. Corpo é a lista de traccarUserId's que useUsers() (GET
// /api/users direto no Traccar) já resolveu — o backend nunca redescobre
// essa lista sozinho, só resolve os campos extra para os ids recebidos
// (ver comentário em UsersService#summaryForTraccarIds no gpsdrive-backend).
export interface UserRowSummary {
  id: number;
  accountType: AccountType | null;
  avatarUrl: string | null;
  document: string | null;
  phone: string | null;
  vehicleCount: number;
  recurrenceValue: number | null;
  // "Situação" (pedido 2026-09-12, revisado no mesmo dia: nunca mais lido
  // de `disabled` do Traccar — ver comentário em UsersPage#UserRow. Soft,
  // nunca bloqueia login, só o que o app do cliente mostra).
  suspended: boolean;
  // Último acesso (2026-09-29). Opcionais: o snapshot em localStorage de
  // antes desta versão não tem esses campos.
  lastSeenAt?: string | null;
  lastSeenDevice?: "android" | "ios" | "web" | null;
  lastLoginAt?: string | null;
  logins30d?: number;
}

// Até 500 ids por chamada (mesmo teto do backend) — bem acima de qualquer
// tabela de Usuários real hoje; existe só para nunca mandar um body
// gigante se a base crescer muito no futuro.
const MAX_IDS_PER_REQUEST = 500;

// Snapshot pra tabela nascer com o resumo real no F5/login (mesmo
// padrão/motivo de gpsdrive_users_list_snapshot_cache_2026-09-04 em
// users.service.ts) — mapa id→resumo, escrito a cada summary() bem
// sucedido (mescla, nunca substitui: um F5 com só parte da lista ainda
// carregada não apaga o que já se sabia de outros usuários).
const USERS_SUMMARY_CACHE_KEY = "gpsdrive.usersSummary.cache.v1";

export function readUsersSummaryCache(): Record<number, UserRowSummary> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(USERS_SUMMARY_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Record<number, UserRowSummary>) : {};
  } catch {
    return {};
  }
}

function writeUsersSummaryCache(rows: UserRowSummary[]): void {
  if (typeof window === "undefined") return;
  try {
    const merged = readUsersSummaryCache();
    for (const row of rows) merged[row.id] = row;
    window.localStorage.setItem(USERS_SUMMARY_CACHE_KEY, JSON.stringify(merged));
  } catch {
    // localStorage indisponível (aba anônima/quota) — cache é só otimização.
  }
}

export function clearUsersSummaryCache(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(USERS_SUMMARY_CACHE_KEY);
  } catch {
    /* noop */
  }
}

export const gpsDriveUsersSummaryService = {
  async summary(traccarUserIds: number[]): Promise<UserRowSummary[]> {
    if (traccarUserIds.length === 0) return [];
    const batches: number[][] = [];
    for (let i = 0; i < traccarUserIds.length; i += MAX_IDS_PER_REQUEST) {
      batches.push(traccarUserIds.slice(i, i + MAX_IDS_PER_REQUEST));
    }
    const results = await Promise.all(
      batches.map((traccarUserIdsBatch) =>
        gpsDrivePost<UserRowSummary[]>("/api/v1/users/summary", { traccarUserIds: traccarUserIdsBatch }),
      ),
    );
    const flat = results.flat();
    writeUsersSummaryCache(flat);
    return flat;
  },
};
